import {
  type Chart,
  type ChartOptions,
  type ScaleOptionsByType,
} from "chart.js";
import { addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { debounce } from "lodash";
import moment from "moment";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { ANNOTATIONS_DATE_FORMAT } from "@/constants/date";
import { type AnnotationDataset } from "@features/custom-annotations";
import {
  CUSTOM_ANNOTATION_ID,
  drawAnnotationIcons,
  getHasDateInCluster,
} from "@features/custom-annotations/annotation-chart-plugin/annotation-plugin-helpers";
import { type User } from "@internal/user";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  convertStartDateToTimeZone,
  iterateHourlyOverIntervalWithAdditionalAccuracy,
  metrics,
  type CurrencyCode,
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import {
  CHANGE_CHART_DATA_PLUGIN_ID,
  getChartDataManagerOptions,
  type ChartDataManagerPluginOptions,
  type CustomChartDataset,
} from "../charts/plugins/chart-data-manager-plugin";
import {
  Y_SCALE_TITLES_PLUGIN_ID,
  type YScaleTitlesPluginPluginOptions,
} from "../charts/plugins/y-scale-titles-plugin";

import { createDiagonalPattern } from "./canvas";
import { type ChartDatasets } from "./chart-preprocessing";
import { customTooltip } from "./tooltip";

export const MAIN_METRIC_ID = "mainMetric" as const;
export const COMPARE_METRIC_1_ID = "compare1" as const;
export const COMPARE_METRIC_2_ID = "compare2" as const;
export const MAIN_METRIC_BENCHMARK_ID = "mainMetricBenchmark" as const;
export const COMPARE_METRIC_1_BENCHMARK_ID = "compare1Benchmark" as const;
export const COMPARE_METRIC_2_BENCHMARK_ID = "compare2Benchmark" as const;
export const METRIC_ORDER = [
  MAIN_METRIC_ID,
  COMPARE_METRIC_1_ID,
  COMPARE_METRIC_2_ID,
] as const;
export type Y_AXIS_ID =
  | typeof MAIN_METRIC_ID
  | typeof COMPARE_METRIC_1_ID
  | typeof COMPARE_METRIC_2_ID
  | typeof MAIN_METRIC_BENCHMARK_ID
  | typeof COMPARE_METRIC_1_BENCHMARK_ID
  | typeof COMPARE_METRIC_2_BENCHMARK_ID;

export const COMPARE_METRIC_1_LINE_DASH = [6, 2];
export const COMPARE_METRIC_1_BAR_CHART_PATTERN = createDiagonalPattern({
  distance: 1.5,
});
export const createCompare1BarChartPattern = (color: string) =>
  createDiagonalPattern({ distance: 1.5, color });

export const COMPARE_METRIC_2_LINE_DASH = [15, 3];
export const COMPARE_METRIC_2_BAR_CHART_PATTERN = createDiagonalPattern({
  distance: 0.5,
});
export const createCompare2BarChartPattern = (color: string) =>
  createDiagonalPattern({ distance: 0.5, color });

export const CHART_PADDING = {
  top: 36,
  left: 6,
  right: 6,
  bottom: 36,
} as const;

const mainMetricScaleOption: ScaleOptionsByType<any> = {
  position: "left",
  axis: "y",
  beginAtZero: true,
  grid: {
    drawTicks: false,
    drawBorder: false,
    drawOnChartArea: true,
  },
  ticks: {
    maxTicksLimit: 6,
    padding: 3,
    font: {
      family: "Commons Pro Mono",
      size: 11,
    },
  },
  title: {
    // This is hidden because it is displayed on the axis using the plugin instead
    display: false,
  },
};

const compareScaleOption: ScaleOptionsByType<any> = {
  position: "right",
  axis: "y",
  beginAtZero: true,
  grid: {
    display: false,
    drawTicks: false,
    drawBorder: false,
    drawOnChartArea: false,
  },
  ticks: {
    maxTicksLimit: 6,
    padding: 3,
    font: {
      family: "Commons Pro Mono",
      size: 11,
    },
  },
  title: {
    // This is hidden because it is displayed on the axis using the plugin instead
    display: true,
  },
};

const benchmarkScaleOption: ScaleOptionsByType<any> = {
  position: "left",
  axis: "y",
  beginAtZero: true,
  grid: {
    display: false,
    drawTicks: false,
    drawBorder: false,
    drawOnChartArea: true,
  },
  ticks: {
    display: false,
    maxTicksLimit: 6,
    padding: 3,
    font: {
      family: "Commons Pro Mono",
      size: 11,
    },
  },
  title: {
    // This is hidden because it is displayed on the axis using the plugin instead
    display: false,
  },
};

const getXScaleOption = (
  timeGranularity: ITimeGranularityV2,
): ScaleOptionsByType<any> => ({
  axis: "x",
  grid: {
    drawOnChartArea: false,
    drawBorder: false,
    drawTicks: false,
  },
  ticks: {
    padding: 20,
    maxRotation: 0,
    autoSkip: true,
    font: {
      family: "Commons Pro",
      size: 13,
    },
    ...(timeGranularity === "hourly" && {
      major: {
        enabled: true,
      },
    }),
  },
  type: "timeseries",
  time: {
    unit: (
      {
        hourly: "hour",
        daily: "day",
        weekly: "week",
        monthly: "month",
      } as const
    )[timeGranularity],
  },
});

export const debouncedResize = debounce((chart: Chart | ChartJSOrUndefined) => {
  if (chart?.ctx) {
    drawAnnotationIcons(chart, { force: true });
    chart.update();
  }
}, 100);

export const onCustomAnnotationHover = function (all: {
  chart: Chart;
  x: number;
  y: number;
  native: PointerEvent;
}) {
  const points = all.chart.getElementsAtEventForMode(
    all as any,
    "nearest",
    { intersect: true },
    true,
  );

  if (points?.length) {
    (all?.native?.target as HTMLElement).className = "cursor-copy";
  } else {
    (all?.native?.target as HTMLElement).className = "";
  }
};

export const onCustomAnnotationClick = function (all: {
  chart: Chart;
  x: number;
  y: number;
  native: PointerEvent;
}) {
  const points = all.chart.getElementsAtEventForMode(
    all as any,
    "nearest",
    { intersect: true },
    true,
  );

  const chosenPoint = points?.[0];
  if (!chosenPoint) {
    return;
  }

  const annotationDataset = all.chart.data.datasets?.find(
    (meta) => meta.label === CUSTOM_ANNOTATION_ID,
  ) as AnnotationDataset | undefined;

  let dateLabel: string = all.chart.data.labels?.[chosenPoint.index] as string;

  const chartDataManagerOptions = getChartDataManagerOptions(all.chart);
  const comparison1 = chartDataManagerOptions.chartConfig?.comparisons?.[0];
  const { compareDateRangeLabels } = chartDataManagerOptions;
  const { timezone } = chartDataManagerOptions.pageConfiguration;
  if (
    chosenPoint?.datasetIndex > 0 &&
    comparison1?.queryKey === "compareDateRange" &&
    compareDateRangeLabels
  ) {
    dateLabel = compareDateRangeLabels[chosenPoint.index].toISOString();
  }

  if (annotationDataset?.metadata) {
    const { showAnnotations, createAnnotation, annotatedDates } =
      annotationDataset.metadata;
    const dateLabelFormatted = formatInTimeZone(
      dateLabel,
      timezone,
      ANNOTATIONS_DATE_FORMAT,
    );
    const hasDate = getHasDateInCluster([dateLabelFormatted], annotatedDates);
    if (hasDate) {
      showAnnotations({
        startDate: dateLabelFormatted,
        endDate: formatInTimeZone(
          addDays(new Date(dateLabel), 1),
          timezone,
          ANNOTATIONS_DATE_FORMAT,
        ),
      });
    } else {
      createAnnotation(
        formatInTimeZone(dateLabel, timezone, ANNOTATIONS_DATE_FORMAT),
      );
    }
  }
};

export const getChartOptions = ({
  processedData,
  processedDataCompare1,
  processedDataCompare2,
  benchmarkMainData,
  benchmarkCompare1Data,
  benchmarkCompare2Data,
  selectedMetrics,
  selectedCompareMetrics,
  timeGranularity,
  animation,
  code,
}: {
  processedData: ChartDatasets;
  processedDataCompare1: ChartDatasets | null;
  processedDataCompare2: ChartDatasets | null;
  benchmarkMainData: number | null;
  benchmarkCompare1Data: number | null;
  benchmarkCompare2Data: number | null;
  selectedMetrics: SalesPageMetric[];
  selectedCompareMetrics: SalesPageMetric[];
  timeGranularity: ITimeGranularityV2;
  code: CurrencyCode;
  animation: boolean;
}): ChartOptions<any> => {
  const [mainMetric, compare1Metric, compare2Metric] = selectedMetrics;
  const [selectedCompare1Metric, selectedCompare2Metric] =
    selectedCompareMetrics;
  const aggregateDataset: metrics.MetricSeries = {};

  let mainDatasetMaxValue = 0;
  let compare1DatasetMaxValue = 0;
  let compare2DatasetMaxValue = 0;

  processedData.datasets.forEach((dataset) => {
    Object.entries(dataset.series).forEach(([dateLabel, row]) => {
      metrics.addToMetricSeries(aggregateDataset, dateLabel, row);
    });
  });

  mainDatasetMaxValue = Math.max(
    ...Object.values(aggregateDataset).map((row) =>
      calculateMetricValue(mainMetric, row),
    ),
    benchmarkMainData ?? 0,
  );

  if (processedDataCompare1) {
    const compare1AggregateDataset: metrics.MetricSeries = {};
    const metricToUse = compare1Metric || selectedCompare1Metric;

    processedDataCompare1.datasets.forEach((dataset) => {
      Object.entries(dataset.series).forEach(([dateLabel, row]) => {
        metrics.addToMetricSeries(compare1AggregateDataset, dateLabel, row);
      });
    });

    compare1DatasetMaxValue = Math.max(
      ...Object.values(compare1AggregateDataset).map((row) =>
        calculateMetricValue(metricToUse, row),
      ),
      benchmarkCompare1Data ?? 0,
    );

    if (metricToUse === selectedCompare1Metric) {
      mainDatasetMaxValue = Math.max(
        mainDatasetMaxValue,
        compare1DatasetMaxValue,
      );
    }
  }

  if (processedDataCompare2) {
    const compare2AggregateDataset: metrics.MetricSeries = {};
    const metricToUse = compare2Metric || selectedCompare2Metric;

    processedDataCompare2.datasets.forEach((dataset) => {
      Object.entries(dataset.series).forEach(([dateLabel, row]) => {
        metrics.addToMetricSeries(compare2AggregateDataset, dateLabel, row);
      });
    });

    compare2DatasetMaxValue = Math.max(
      ...Object.values(compare2AggregateDataset).map((row) =>
        calculateMetricValue(metricToUse, row),
      ),
      benchmarkCompare2Data ?? 0,
    );

    if (metricToUse === selectedCompare2Metric) {
      mainDatasetMaxValue = Math.max(
        mainDatasetMaxValue,
        compare2DatasetMaxValue,
      );
    }
  }

  return {
    animation,
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
      colors: {
        enabled: true,
      },
      tooltip: {
        enabled: false,
        mode: "nearest",
        intersect: true,
        external: customTooltip(code),
      },
      [Y_SCALE_TITLES_PLUGIN_ID]: {
        font: {
          family: "Inter",
          size: 12,
          weight: "600",
        },
        titles: {},
      } as YScaleTitlesPluginPluginOptions,
      [CHANGE_CHART_DATA_PLUGIN_ID]: {
        data: { datasets: [] },
        labels: [],
        comparisonDatasets: [],
        activeTab: "breakdown",
        selectedMetrics: [],
        chartConfig: {
          chartView: "aggregate",
          chartType: "line",
          mainMetric: {
            metricName: "Spend",
            filters: [],
          },
          comparisons: [],
        },
        annotationDataset: null,
        getCompareQueryMetadata: () => ({
          label: "",
          icon: <></>,
          optionLabel: "",
        }),
        pageConfiguration: {},
      } as unknown as ChartDataManagerPluginOptions,
    },
    layout: {
      padding: { ...CHART_PADDING },
    },
    onResize(chart: Chart) {
      // on resize, reload the annotations
      debouncedResize(chart);
    },
    onHover: onCustomAnnotationHover,
    onClick: onCustomAnnotationClick,
    scales: {
      mainMetric: {
        ...mainMetricScaleOption,
        suggestedMax: mainDatasetMaxValue,
      },
      compare1: {
        ...compareScaleOption,
        suggestedMax: compare1DatasetMaxValue,
      },
      compare2: {
        ...compareScaleOption,
        suggestedMax: compare2DatasetMaxValue,
      },
      mainMetricBenchmark: {
        ...benchmarkScaleOption,
        suggestedMax: mainDatasetMaxValue,
      },
      compare1Benchmark: {
        ...benchmarkScaleOption,
        suggestedMax: compare1DatasetMaxValue,
      },
      compare2Benchmark: {
        ...benchmarkScaleOption,
        suggestedMax: compare2DatasetMaxValue,
      },
      x: getXScaleOption(timeGranularity),
    },
  };
};

export const isBarChartCheck = (chart: Chart) => {
  const chartType = chart.config?.type;
  if (chartType === "bar") {
    return true;
  }
  return false;
};

export const isLineChartCheck = (chart: Chart) => {
  const chartType = chart.config?.type;
  if (chartType === "line") {
    return true;
  }
  return false;
};

export const getDateOnly = (date: string, timezone: User["timezone"]): Date => {
  const datetime = new Date(date);
  // Return early if the date is timezone aware
  if (date === datetime.toISOString()) {
    return datetime;
  }

  return convertStartDateToTimeZone(date, timezone);
};

export const calculateMaxHeight = (
  numOfRows: number,
  rowHeight: number,
  maxRowCount: number,
) => Math.min(maxRowCount * rowHeight, numOfRows * rowHeight);

export function generateDateLabels(
  { startDate, endDate }: DateInterval,
  timezone: User["timezone"],
  timeGranularity: ITimeGranularityV2,
): Date[] {
  if (timeGranularity === "hourly") {
    const hourlyDates = Array.from(
      iterateHourlyOverIntervalWithAdditionalAccuracy(
        { startDate, endDate },
        timezone,
      ),
    );

    return hourlyDates.map((h) => new Date(h));
  }
  const dateLabels: Date[] = [];
  const currentDate = moment(convertStartDateToTimeZone(startDate, timezone));

  if (timeGranularity === "weekly") {
    currentDate.startOf("week");
  }

  if (timeGranularity === "monthly") {
    currentDate.startOf("month");
  }

  const currentEndDate = moment.tz(endDate, timezone).set({
    hours: moment().hours(),
    minutes: moment().minutes(),
  });

  while (currentDate.isSameOrBefore(currentEndDate)) {
    const label = currentDate.toDate();
    dateLabels.push(label);

    if (timeGranularity === "daily") {
      currentDate.add(1, "day");
    } else if (timeGranularity === "weekly") {
      currentDate.add(1, "week");
    } else if (timeGranularity === "monthly") {
      currentDate.add(1, "month");
    }
  }

  return dateLabels;
}

export const buildDataset = ({
  data,
  yAxisID,
  borderColor,
  backgroundColor,
  borderDash,
  getIcon,
  createBackgroundColor,
  name = "",
  compare,
  ...rest
}: CustomChartDataset) => ({
  yAxisID,
  xAxisID: "x",
  data,
  borderWidth: 2,
  hitRadius: 100,
  maxBarThickness: 60,
  pointRadius: data.length === 1 ? 3 : 0,
  pointHoverRadius: 5,
  pointBackgroundColor: borderColor,
  pointBorderColor: borderColor,
  createBackgroundColor,
  borderColor,
  backgroundColor,
  borderDash,
  getIcon,
  name,
  compare,
  ...rest,
});

// TODO: Add tests for this function
export const buildCompareDateRangeData = ({
  labels,
  metricSeries,
  metric,
  compareDateRange,
  timeGranularity,
  timezone,
}: {
  labels: Date[];
  metricSeries: metrics.MetricSeries;
  metric: SalesPageMetric;
  compareDateRange?: DateInterval;
  timeGranularity: ITimeGranularityV2;
  timezone: string;
}): number[] => {
  if (labels.length && compareDateRange) {
    const compareLabels = generateDateLabels(
      compareDateRange,
      timezone,
      timeGranularity,
    );

    /* This is for if the compare date range number of days are not equal to
       the number of days in the main date range */
    return labels.map((_label, index) => {
      const date = compareLabels[index];

      if (!date) {
        return 0;
      }
      return calculateMetricValue(metric, metricSeries[date.toISOString()]);
    });
  }
  return [];
};

export const calculateMetricValue = (
  metric: SalesPageMetric,
  data: metrics.MetricPoint | undefined,
) => {
  if (!metric || !data) {
    return 0;
  } else if (data && data[metric.id as keyof metrics.MetricPoint]) {
    return data[metric.id as keyof metrics.MetricPoint] as number;
  }

  return metric.calculate(data);
};
